import { computed } from 'vue';
import {
	CampaignEditorGetResultDto, CampaignManagementAPIClient, ChangesExtractor,
	DeliveryType, OriginalCampaignDataDto, CampaignChange, CampaignChangeEnum,
	CampaignAttachEntitiesChange, CampaignDetachEntitiesChange
} from '@studyportals/campaign-management-api-interface';
import WsManager from '@/infrastructure/ws-manager';
import { ModalErrorStates } from '@/models/modal/modal.enum';
import { HttpErrorResponse } from '@/models/error.interface';
import { NotificationStatuses } from '@/models/notifications.interface';
import { LoadingStatuses } from '@/models/loading.interface';
import { IOverlappingCampaignsResult } from '@/models/overlapping-campaigns.interface';
import { IDuplicateEntryErrorResult } from '@/models/error.interface';
import { Routes } from '@/models/router/router.interface';
import { CampaignFeaturesForTracking } from '@/models/tracking.interface';
import { IDuplicateEntryCampaignDetails } from '@/models/duplicate-entry-campaigns.interface';
import cookieHandler from '@/infrastructure/cookie-handler';
import rollbarOperator from '@/infrastructure/rollbar-operator';
import campaignErrorPresenter from '@/presenters/campaign-error-presenter';
import savingValidationPayloadPresenter from '@/presenters/saving-validation-payload-presenter';
import availableEntitiesPresenter from '@/presenters/available-entities-presenter';
import validationPresenter from '@/presenters/validation-presenter';
import unlinkOrganisationWarningPresenter from '@/presenters/unlink-organisation-warning-presenter';
import redshiftTracker from '@/infrastructure/redshift-tracker';
import CampaignErrorPresenter from '@/presenters/campaign-error-presenter';
import store from '@/store';

class CampaignPresenter {
	private _client: CampaignManagementAPIClient | null = null;
	private readonly tokenId: string;

	private campaignId = computed((): number => {
		const router = store.getters.router();
		return router.props.id;
	});

	private originalCampaignData = computed((): OriginalCampaignDataDto | null => {
		return store.getters.originalCampaignData();
	});

	private notificationStatus = computed((): NotificationStatuses => {
		return store.getters.notificationStatus();
	});

	private get client(): CampaignManagementAPIClient {
		if (!this._client) {
			this._client = new CampaignManagementAPIClient(
				webpackDefinitions.CAMPAIGN_MANAGEMENT_API_URL,
				WsManager.getBus()
			);
		}
		return this._client;
	}

	constructor() {
		this.tokenId = cookieHandler.retrieveTokenIdCookie();
	}

	public updateOriginalCampaignData(): void {
		store.mutations.setOriginalCampaignData(
			savingValidationPayloadPresenter.createSavingPayload() as OriginalCampaignDataDto
		);
	}

	public hasCampaignDataChanged(): boolean {
		const campaignChanges = this.extractCampaignChanges();
		return campaignChanges.length > 0;
	}

	public retrieveAttachedEntityChanges(): CampaignAttachEntitiesChange | undefined {
		const campaignChanges = this.extractCampaignChanges();
		return campaignChanges.find((change) => change.type === CampaignChangeEnum.ATTACH_ENTITIES);
	}

	public retrieveDetachedEntityChanges(): CampaignDetachEntitiesChange | undefined {
		const campaignChanges = this.extractCampaignChanges();
		return campaignChanges.find((change) => change.type === CampaignChangeEnum.DETACH_ENTITIES);
	}

	public async prepareAccountManagers(): Promise<void> {
		store.mutations.setLoadingStatusAccountManagers(LoadingStatuses.ACTIVE);

		let wasSuccessful = false;
		try {
			const result = await this.client.getAccountManagers(this.tokenId);
			const people = result
				.map((person) => ({
					label: person.name,
					value: person.id
				}))
				.sort((a, b) => a.label.localeCompare(b.label));
			store.mutations.setAllAccountManagers(people);
			store.mutations.setLoadingStatusAccountManagers(LoadingStatuses.INACTIVE);
			wasSuccessful = true;
		} catch (e) {
			rollbarOperator.triggerError(e as Error);
			store.mutations.setLoadingStatusAccountManagers(LoadingStatuses.FAILURE);
		} finally {
			redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.ACCOUNT_MANAGERS, false, wasSuccessful);
		}
	}

	public async prepareClientServicesPeople(): Promise<void> {
		store.mutations.setLoadingStatusClientServicesPeople(LoadingStatuses.ACTIVE);

		let wasSuccessful = false;
		try {
			const result = await this.client.getClientServicesUsers(this.tokenId);
			const people = result
				.map((person) => ({
					label: person.name,
					value: person.id
				}))
				.sort((a, b) => a.label.localeCompare(b.label));
			store.mutations.setAllClientServicesPeople(people);
			store.mutations.setLoadingStatusClientServicesPeople(LoadingStatuses.INACTIVE);
			wasSuccessful = true;
		} catch (e) {
			rollbarOperator.triggerError(e as Error);
			store.mutations.setLoadingStatusClientServicesPeople(LoadingStatuses.FAILURE);
		} finally {
			redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.CLIENT_SERVICES_PEOPLE, false, wasSuccessful);
		}
	}

	public async prepareCampaignDetails(): Promise<void> {
		if (!this.campaignId.value || isNaN(this.campaignId.value)) {
			store.mutations.setModalErrorState(ModalErrorStates.INVALID_CAMPAIGN_ID);
			return;
		}

		store.mutations.setLoadingStatusPrimaryData(LoadingStatuses.ACTIVE);

		let wasSuccessful = false;
		let unsupportedCampaignSettingDetected = false;
		try {
			const details = await this.client.getCampaignForCampaignEditor(this.campaignId.value, this.tokenId);

			store.mutations.setName(details.name);
			store.mutations.setCampaignLiftExcluded(details.isExcludedFromCampaignLift);
			store.mutations.setPersistGLParamRefLink(details.persistGLParamRefLink);
			store.mutations.setRegion(details.region);
			store.mutations.setLinkedOrganisations(details.linkedOrganisations);
			store.mutations.setInternalNotes(details.internalNotes);

			this.setStartDateBasedOnCampaignDetails(details);
			this.setEndDateBasedOnCampaignDetails(details);
			this.setBudgetListingBasedOnCampaignDetails(details);
			this.setBudgetTotalBasedOnCampaignDetails(details);
			this.setCustomBudgetBasedOnCampaignDetails(details);
			this.setCurrencyBasedOnCampaignDetails(details);
			this.setPartnershipTypeBasedOnCampaignDetails(details);
			this.setCampaignTypeBasedOnCampaignDetails(details);
			this.setDeliveryModeBasedOnCampaignDetails(details);
			this.setCrmIdBasedOnCampaignDetails(details);
			this.setAccountManagerBasedOnCampaignDetails(details);
			this.setClientServicesPersonBasedOnCampaignDetails(details);
			store.mutations.setLoadingStatusPrimaryData(LoadingStatuses.INACTIVE);
			wasSuccessful = true;
		} catch (error) {
			unsupportedCampaignSettingDetected = CampaignErrorPresenter.isErrorRelatedToUnsupportedProperty(error as HttpErrorResponse);
			campaignErrorPresenter.handleCampaignDetailsRetrievalError(error as HttpErrorResponse);
			store.mutations.setLoadingStatusPrimaryData(LoadingStatuses.FAILURE);
		} finally {
			if (!unsupportedCampaignSettingDetected) {
				redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.GENERAL, false, wasSuccessful);
			}
		}
	}

	public async retrieveCampaignListingEntities(): Promise<void> {
		if (!this.campaignId.value) {
			store.mutations.setLoadingStatusEntities(LoadingStatuses.FAILURE);
			return;
		}

		store.mutations.setLoadingStatusEntities(LoadingStatuses.ACTIVE);

		let wasSuccessful = false;
		try {
			const rawEntities = await this.client.getCampaignListingEntities(this.campaignId.value, this.tokenId);
			const presentableEntities = availableEntitiesPresenter.convertToPresentableEntities(rawEntities);
			const sortedEntities = availableEntitiesPresenter.sortPresentableEntities(presentableEntities);
			store.mutations.setAvailableEntities(sortedEntities);
			store.mutations.setLoadingStatusEntities(LoadingStatuses.INACTIVE);
			wasSuccessful = true;
		} catch (error) {
			rollbarOperator.triggerError(error as Error);
			store.mutations.setLoadingStatusEntities(LoadingStatuses.FAILURE);
			store.mutations.setModalErrorState(ModalErrorStates.GET_CAMPAIGN_LISTING_ENTITIES);
		} finally {
			redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.LISTING_ENTITIES, false, wasSuccessful);
		}
	}

	public async retrieveCustomProducts(): Promise<void> {
		if (!this.campaignId.value) {
			store.mutations.setLoadingStatusCustomProducts(LoadingStatuses.FAILURE);
			return;
		}

		store.mutations.setLoadingStatusCustomProducts(LoadingStatuses.ACTIVE);

		let wasSuccessful = false;
		let unsupportedCampaignSettingDetected = false;
		try {
			const products = await this.client.getCustomProducts(this.campaignId.value, this.tokenId);
			store.mutations.setCustomProducts(products);
			store.mutations.setLoadingStatusCustomProducts(LoadingStatuses.INACTIVE);
			/* Though all flows should already cover updating original campaign data after data was fetched,
			it's done once more here, because otherwise the "hasCampaignDataChanged" will be triggered before the
			original campaign data could update, which breaks the tool. */
			this.updateOriginalCampaignData();
			wasSuccessful = true;
		} catch (error) {
			unsupportedCampaignSettingDetected = CampaignErrorPresenter.isErrorRelatedToUnsupportedProperty(error as HttpErrorResponse);
			rollbarOperator.triggerError(error as Error);
			store.mutations.setLoadingStatusCustomProducts(LoadingStatuses.FAILURE);
			store.mutations.setModalErrorState(ModalErrorStates.GET_CUSTOM_PRODUCTS);
		} finally {
			if (!unsupportedCampaignSettingDetected) {
				redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.CUSTOM_PRODUCTS, false, wasSuccessful);
			}
		}
	}

	public async retrieveRightsUsers(): Promise<void> {
		if (!this.campaignId.value) {
			store.mutations.setLoadingStatusRights(LoadingStatuses.FAILURE);
			return;
		}

		store.mutations.setLoadingStatusRights(LoadingStatuses.ACTIVE);

		let wasSuccessful = false;
		try {
			const users = await this.client.getUsersByCampaignID(this.campaignId.value, this.tokenId);
			store.mutations.setRightsUsers(users);
			store.mutations.setLoadingStatusRights(LoadingStatuses.INACTIVE);
			wasSuccessful = true;
		} catch (error) {
			rollbarOperator.triggerError(error as Error);
			store.mutations.setLoadingStatusRights(LoadingStatuses.FAILURE);
			store.mutations.setModalErrorState(ModalErrorStates.GET_RIGHTS_USERS);
		} finally {
			redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.RIGHTS, false, wasSuccessful);
		}
	}

	public async updateCampaignReadinessInfo(): Promise<void> {
		if (!this.campaignId.value) {
			store.mutations.setLoadingStatusCampaignReadinessInfo(LoadingStatuses.FAILURE);
			return;
		}

		store.mutations.setLoadingStatusCampaignReadinessInfo(LoadingStatuses.ACTIVE);

		let wasSuccessful = false;
		try {
			const result = await this.client.getCampaignValidationState(this.campaignId.value, this.tokenId);
			store.mutations.setCampaignReadinessInfo(result);
			store.mutations.setLoadingStatusCampaignReadinessInfo(LoadingStatuses.INACTIVE);
			wasSuccessful = true;
		} catch (error) {
			rollbarOperator.triggerError(error as Error);
			store.mutations.setLoadingStatusCampaignReadinessInfo(LoadingStatuses.FAILURE);
			store.mutations.setModalErrorState(ModalErrorStates.GET_CAMPAIGN_READINESS_INFO);
		} finally {
			redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.CAMPAIGN_READINESS_INFO, false, wasSuccessful);
		}
	}

	public async createCampaign(): Promise<void> {
		const payload = savingValidationPayloadPresenter.createCreationPayload();
		if (!payload) {
			store.mutations.setNotificationStatus(NotificationStatuses.CREATION_ERROR);
			redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.CREATION, true, false);
			return;
		}

		store.mutations.setNotificationStatus(NotificationStatuses.CREATION_IN_PROGRESS);
		store.mutations.resetValidationInfoDates();

		let wasSuccessful = false;
		try {
			const campaignId = await this.client.createCampaign(payload, this.tokenId);

			store.mutations.setNotificationStatus(NotificationStatuses.CREATION_SUCCESS);
			store.mutations.setCurrentRoute(Routes.GENERAL, campaignId);
			store.mutations.setIsCampaignCreation(false);
			wasSuccessful = true;
		} catch (error) {
			this.checkWhetherDuplicateEntryCausedCreationToFail(error as HttpErrorResponse);
			rollbarOperator.triggerError(error as Error);
			validationPresenter.updateValidationInfoDates(error as HttpErrorResponse);
			store.mutations.setNotificationStatus(NotificationStatuses.CREATION_ERROR);
		} finally {
			redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.CREATION, true, wasSuccessful);
		}
	}

	public async saveCampaignDetails(): Promise<void> {
		if (!this.campaignId.value) {
			store.mutations.setModalErrorState(ModalErrorStates.INVALID_CAMPAIGN_ID);
			return;
		}

		const campaignChanges = this.extractCampaignChanges();
		if (!unlinkOrganisationWarningPresenter.canSaveProceedBasedOnUnlinkedOrganisationsData(campaignChanges)) {
			return;
		}

		store.mutations.setNotificationStatus(NotificationStatuses.SAVING_IN_PROGRESS);
		store.mutations.resetValidationInfoDates();

		let wasSuccessful = false;
		try {
			await this.client.saveCampaign(
				this.campaignId.value,
				campaignChanges,
				this.tokenId
			);

			wasSuccessful = true;
		} catch (error) {
			this.checkWhetherOverlappingCampaignsCausedSavingToFail(error as HttpErrorResponse);
			this.checkWhetherDuplicateEntryCausedSavingToFail(error as HttpErrorResponse);
			rollbarOperator.triggerError(error as Error);
			validationPresenter.updateValidationInfoDates(error as HttpErrorResponse);
			store.mutations.setNotificationStatus(NotificationStatuses.SAVING_ERROR);
		} finally {
			redshiftTracker.trackProcessOutcome(CampaignFeaturesForTracking.GENERAL, true, wasSuccessful);
		}

		if (!wasSuccessful) {
			return;
		}

		await this.updateCampaignReadinessInfo();
		await this.updateCampaignDataAfterSuccessfulSave(campaignChanges);
		store.mutations.setNotificationStatus(NotificationStatuses.SAVING_SUCCESS);
		store.mutations.resetIdsOfModifiedCustomProducts();
	}

	private async updateCampaignDataAfterSuccessfulSave(changes: CampaignChange[]): Promise<void> {
		const customProductsChangeTypes = [
			CampaignChangeEnum.ADD_CUSTOM_PRODUCTS,
			CampaignChangeEnum.REMOVE_CUSTOM_PRODUCTS,
			CampaignChangeEnum.UPDATE_CUSTOM_PRODUCTS
		];
		const listingEntityChangeTypes = [
			CampaignChangeEnum.ATTACH_ENTITIES,
			CampaignChangeEnum.DETACH_ENTITIES,
			CampaignChangeEnum.LINK_ORGANISATIONS,
			CampaignChangeEnum.UNLINK_ORGANISATIONS
		];
		const rightsChangeTypes = [
			CampaignChangeEnum.GRANT_ACCESS,
			CampaignChangeEnum.REVOKE_ACCESS
		];
		const genericChangeTypes = [
			CampaignChangeEnum.START_DATE,
			CampaignChangeEnum.END_DATE,
			CampaignChangeEnum.NAME,
			CampaignChangeEnum.CRM_ID,
			CampaignChangeEnum.REGION,
			CampaignChangeEnum.HANDLER,
			CampaignChangeEnum.CLIENT_SERVICES,
			CampaignChangeEnum.PARTNERSHIP_TYPE,
			CampaignChangeEnum.LISTING_BUDGET,
			CampaignChangeEnum.DELIVERY_MODE,
			CampaignChangeEnum.CUSTOM_PRODUCTS_BUDGET,
			CampaignChangeEnum.CAMPAIGN_LIFT_EXCLUDED,
			CampaignChangeEnum.LINK_ORGANISATIONS,
			CampaignChangeEnum.UNLINK_ORGANISATIONS,
			CampaignChangeEnum.PERSIST_GL_PARAM_REF_LINK,
			CampaignChangeEnum.INTERNAL_NOTES,
			CampaignChangeEnum.SET_GEOTARGETING
		];
		const customProductsChanges = changes.filter((change) => customProductsChangeTypes.includes(change.type));
		const listingEntityChanges = changes.filter((change) => listingEntityChangeTypes.includes(change.type))
		const rightsChanges = changes.filter((change) => rightsChangeTypes.includes(change.type));
		const genericChanges = changes.filter((change) => genericChangeTypes.includes(change.type));
		/* Since some change types are part of multiple groups, we need to separately check whether any unexpected changes were found. */
		const unexpectedChanges = changes.filter((change) =>
			!customProductsChanges.find((recognisedChange) => recognisedChange.type === change.type) &&
			!listingEntityChanges.find((recognisedChange) => recognisedChange.type === change.type) &&
			!rightsChanges.find((recognisedChange) => recognisedChange.type === change.type) &&
			!genericChanges.find((recognisedChange) => recognisedChange.type === change.type)
		);
		if (customProductsChanges.length > 0) {
			await this.retrieveCustomProducts();
		}

		if (listingEntityChanges.length > 0) {
			await this.retrieveCampaignListingEntities();
		}

		if (rightsChanges.length > 0) {
			await this.retrieveRightsUsers();
		}

		if (genericChanges.length > 0) {
			await this.prepareCampaignDetails();
		}

		if (unexpectedChanges.length > 0) {
			throw Error('CampaignPresenter.updateCampaignDetailsAfterSuccessfulSave: unexpected change type detected.');
		}

		this.updateOriginalCampaignData();
	}

	private checkWhetherOverlappingCampaignsCausedSavingToFail(error: HttpErrorResponse): void {
		const errorDetails = (typeof error.response?.text === 'string'
			? JSON.parse(error.response.text)
			: (error.response ?? { cause: { containsOverlappingEntities: false } })) as IOverlappingCampaignsResult;
		if (!errorDetails.cause?.overlappingEntitiesData) {
			return;
		}

		store.mutations.setOverlappingCampaignsCluster(errorDetails.cause.overlappingEntitiesData);
	}

	private checkWhetherDuplicateEntryCausedCreationToFail(error: HttpErrorResponse): void {
		const errorDetails = (typeof error.response?.text === 'string'
			? JSON.parse(error.response.text)
			: (error.response ?? { cause: { id: 0 }, errorCode: 0 })) as IDuplicateEntryErrorResult;
		if (errorDetails.errorCode !== 2408) {
			return;
		}

		if (errorDetails.cause.id && errorDetails.cause.name && errorDetails.cause.startDate) {
			store.mutations.setDuplicateEntryCampaignDetails(errorDetails.cause as IDuplicateEntryCampaignDetails);
		}

		store.mutations.setModalErrorState(ModalErrorStates.CREATE_FAILED_ON_DUPLICATE_ENTRY);
	}

	private checkWhetherDuplicateEntryCausedSavingToFail(error: HttpErrorResponse): void {
		const errorDetails = (typeof error.response?.text === 'string'
			? JSON.parse(error.response.text)
			: (error.response ?? { cause: { id: 0 }, errorCode: 0 })) as IDuplicateEntryErrorResult;
		if (errorDetails.errorCode !== 2409) {
			return;
		}

		if (errorDetails.cause.id && errorDetails.cause.name && errorDetails.cause.startDate) {
			store.mutations.setDuplicateEntryCampaignDetails(errorDetails.cause as IDuplicateEntryCampaignDetails);
		}

		store.mutations.setModalErrorState(ModalErrorStates.SAVE_FAILED_ON_DUPLICATE_ENTRY);
	}

	private setStartDateBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (!details.startDate || !this.isDateValid(details.startDate)) {
			return;
		}

		store.mutations.setStartDate(details.startDate);
	}

	private setEndDateBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (!details.endDate || !this.isDateValid(details.endDate)) {
			return;
		}

		store.mutations.setEndDate(details.endDate);
	}

	private setBudgetListingBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (typeof details.listingBudget as unknown !== 'number') {
			return;
		}

		store.mutations.setBudgetListing(details.listingBudget);
	}

	private setBudgetTotalBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (typeof details.totalBudget as unknown !== 'number') {
			return;
		}

		store.mutations.setBudgetTotal(details.totalBudget);
	}

	private setCustomBudgetBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (typeof details.customProductsBudget as unknown !== 'number') {
			return;
		}

		store.mutations.setBudgetCustom(details.customProductsBudget);
	}

	private setCurrencyBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (!details.currency) {
			return;
		}

		store.mutations.setCurrency(details.currency);
	}

	private setPartnershipTypeBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (!details.partnershipType) {
			return;
		}

		store.mutations.setPartnershipType(details.partnershipType);
	}

	private setCampaignTypeBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (!details.campaignType) {
			return;
		}

		store.mutations.setCampaignType(details.campaignType);
	}

	private setCrmIdBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (!details.crmId) {
			return;
		}

		store.mutations.setCrmId(details.crmId);
	}

	private setAccountManagerBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (!details.accountManager) {
			return;
		}

		store.mutations.setAccountManager(details.accountManager);
	}

	private setClientServicesPersonBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		if (!details.clientServicesPerson) {
			return;
		}

		store.mutations.setClientServicesPerson(details.clientServicesPerson);
	}

	private setDeliveryModeBasedOnCampaignDetails(details: CampaignEditorGetResultDto): void {
		store.mutations.setDeliveryMode(details.deliveryMode.type);

		if (details.deliveryMode.type !== DeliveryType.CUSTOM_OVERPERFORMANCE) {
			return;
		}

		store.mutations.setOverPerformanceLimit(details.deliveryMode.overperformanceLimit);
	}

	private extractCampaignChanges(): CampaignChange[] {
		// Don't check for changes while a campaign is being saved, or it might detect changes that were already processed.
		if (this.notificationStatus.value === NotificationStatuses.SAVING_IN_PROGRESS) {
			return [];
		}

		/* Without the start date check, one of the Cypress tests where the campaign data is mocked
		will fail (for that test, no start date is set so that the validation message can be checked). */
		if (!this.originalCampaignData.value || !this.originalCampaignData.value.baseCampaignData.startDate) {
			return [];
		}

		const newCampaignData = savingValidationPayloadPresenter.createSavingPayload();
		return ChangesExtractor.extractChangesFromDifference(
			this.originalCampaignData.value,
			newCampaignData
		);
	}

	private isDateValid(date: unknown): boolean {
		return !isNaN(date as number) && date instanceof Date;
	}
}

export default new CampaignPresenter();
